import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Edit2, Trash2, Plus, Minus, Loader2, Image as ImageIcon, Download, Upload, FileText } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';

const convertToCSV = (headers, rows) => {
    const csvRows = [headers.join(',')];
    for (const row of rows) {
        const values = headers.map(header => {
            const val = row[header];
            const escaped = ('' + (val !== undefined && val !== null ? val : '')).replace(/"/g, '""');
            return `"${escaped}"`;
        });
        csvRows.push(values.join(','));
    }
    return csvRows.join('\n');
};

const downloadCSV = (filename, content) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const parseCSV = (text) => {
    const lines = [];
    let row = [""];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        const next = text[i+1];
        if (c === '"') {
            if (inQuotes && next === '"') {
                row[row.length - 1] += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (c === ',' && !inQuotes) {
            row.push('');
        } else if ((c === '\r' || c === '\n') && !inQuotes) {
            if (c === '\r' && next === '\n') {
                i++;
            }
            lines.push(row);
            row = [''];
        } else {
            row[row.length - 1] += c;
        }
    }
    if (row.length > 1 || row[0] !== '') {
        lines.push(row);
    }
    return lines;
};

export default function ManageMenu() {
    const toast = useToast();
    const { user } = useAuth();
    const [confirmState, setConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Filtering & Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [isEditingCategory, setIsEditingCategory] = useState(false);
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', imageUrl: '', sortOrder: '' });
    const [activeTab, setActiveTab] = useState('categories'); // 'items' or 'categories'
    
    // Bulk Delete Selection States
    const [selectedItemIds, setSelectedItemIds] = useState(new Set());
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());

    useEffect(() => {
        setSelectedItemIds(new Set());
        setSelectedCategoryIds(new Set());
    }, [activeTab]);

    const itemsPerPage = 10;

    const initialForm = {
        name: '', description: '', price: '', categoryId: '',
        imageUrl: '', isVegetarian: false, isNonVegetarian: false, isSpicy: false, isSweet: false, prepTime: '15', stockQuantity: ''
    };

    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [imageRequired, setImageRequired] = useState(false);

    const handleImageUpload = async (e, type, setter) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);
            uploadData.append('type', type);

            const response = await api.post('/api/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.secureUrl) {
                setter(response.data.secureUrl);
                toast.success('Image uploaded successfully');
            } else {
                toast.error('Failed to upload image');
            }
        } catch (err) {
            console.error('Image upload error:', err);
            toast.error(err.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        if (user?.restaurant_id) {
            fetchData();
        }
    }, [user]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterCategory]);

    const fetchData = async () => {
        const resId = user?.restaurant_id;
        if (!resId) return;
        try {
            const [itemsRes, catRes] = await Promise.all([
                api.get(`/api/menu/items?admin=true&restaurantId=${resId}`),
                api.get(`/api/menu/categories?restaurantId=${resId}`)
            ]);
            setItems(itemsRes.data);
            setCategories(catRes.data);
            if (catRes.data.length > 0 && !formData.categoryId) {
                setFormData(prev => ({ ...prev, categoryId: catRes.data[0].id }));
            }
        } catch (error) {
            console.error('Failed to fetch menu data', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAvailability = async (id, currentStatus) => {
        try {
            await api.patch(`/api/menu/items/${id}/availability`, { isAvailable: !currentStatus });
            fetchData();
        } catch (error) {
            toast.error('Failed to update availability');
        }
    };

    const handleStockUpdate = async (id, newStock) => {
        if (newStock < 0) return;
        try {
            await api.patch(`/api/menu/items/${id}/stock`, { stockQuantity: newStock });
            // Optimistic update — sync is_available with stock level
            setItems(currentItems => currentItems.map(item =>
                item.id === id
                    ? { ...item, stock_quantity: newStock, is_available: newStock > 0 }
                    : item
            ));
            if (newStock === 0) {
                toast.warning('Stock set to 0 — item marked as Out of Stock.');
            } else {
                const wasOutOfStock = items.find(i => i.id === id)?.is_available === false;
                if (wasOutOfStock) toast.success('Stock refilled — item is back In Stock!');
            }
        } catch (error) {
            toast.error('Failed to update stock');
        }
    };

    const handleDelete = (id) => {
        setConfirmState({
            open: true,
            title: 'Delete Menu Item',
            message: 'Are you sure you want to delete this item?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await api.delete(`/api/menu/items/${id}`);
                    toast.success('Item deleted successfully');
                    fetchData();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to delete item');
                } finally {
                    setSubmitting(false);
                    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const downloadTemplate = () => {
        const headers = ['Category', 'Item Name', 'Description', 'Price', 'Is Vegetarian', 'Is Spicy', 'Is Available', 'Stock Quantity', 'Prep Time (min)', 'Image URL'];
        const sampleRows = [
            {
                'Category': 'Starters',
                'Item Name': 'Paneer Tikka',
                'Description': 'Tandoori grilled paneer cubes marinated in spices.',
                'Price': '249.00',
                'Is Vegetarian': 'true',
                'Is Spicy': 'true',
                'Is Available': 'true',
                'Stock Quantity': '50',
                'Prep Time (min)': '15',
                'Image URL': 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800'
            },
            {
                'Category': 'Mains',
                'Item Name': 'Butter Chicken',
                'Description': 'Rich, creamy tomato-based chicken curry.',
                'Price': '349.00',
                'Is Vegetarian': 'false',
                'Is Spicy': 'false',
                'Is Available': 'true',
                'Stock Quantity': '80',
                'Prep Time (min)': '20',
                'Image URL': 'https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=800'
            }
        ];
        const csvContent = convertToCSV(headers, sampleRows);
        downloadCSV('menu_import_template.csv', csvContent);
    };

    const handleExportCatalog = () => {
        const headers = ['Category', 'Item Name', 'Description', 'Price', 'Is Vegetarian', 'Is Spicy', 'Is Available', 'Stock Quantity', 'Prep Time (min)', 'Image URL'];
        
        const rows = items.map(item => ({
            'Category': item.category_name || 'Uncategorized',
            'Item Name': item.name,
            'Description': item.description || '',
            'Price': item.price,
            'Is Vegetarian': item.is_vegetarian ? 'true' : 'false',
            'Is Spicy': item.is_spicy ? 'true' : 'false',
            'Is Available': item.is_available ? 'true' : 'false',
            'Stock Quantity': item.stock_quantity || '100',
            'Prep Time (min)': item.prep_time_minutes || '15',
            'Image URL': item.image_url || ''
        }));
        
        const csvContent = convertToCSV(headers, rows);
        downloadCSV('restaurant_menu_catalog.csv', csvContent);
    };

    const handleImportCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const text = evt.target.result;
                const rows = parseCSV(text);
                if (rows.length < 2) {
                    toast.error('The uploaded CSV file is empty or has no data rows.');
                    return;
                }

                const headers = rows[0].map(h => h.trim().toLowerCase());
                
                // Map headers to column indices
                const categoryIdx = headers.indexOf('category');
                const nameIdx = headers.indexOf('item name');
                const descIdx = headers.indexOf('description');
                const priceIdx = headers.indexOf('price');
                const vegIdx = headers.indexOf('is vegetarian');
                const spicyIdx = headers.indexOf('is spicy');
                const availIdx = headers.indexOf('is available');
                const stockIdx = headers.indexOf('stock quantity');
                const prepIdx = headers.indexOf('prep time (min)');
                const imageIdx = headers.indexOf('image url');

                if (nameIdx === -1 || priceIdx === -1) {
                    toast.error('CSV must contain at least "Item Name" and "Price" columns.');
                    return;
                }

                const importItems = [];

                for (let i = 1; i < rows.length; i++) {
                    const row = rows[i];
                    if (row.length < 2 || !row[nameIdx]) continue;

                    const item = {
                        category: categoryIdx !== -1 ? row[categoryIdx] : 'Uncategorized',
                        name: row[nameIdx],
                        description: descIdx !== -1 ? row[descIdx] : '',
                        price: parseFloat(row[priceIdx]),
                        isVegetarian: vegIdx !== -1 ? (row[vegIdx].toLowerCase() === 'true' || row[vegIdx].toLowerCase() === 'yes') : false,
                        isSpicy: spicyIdx !== -1 ? (row[spicyIdx].toLowerCase() === 'true' || row[spicyIdx].toLowerCase() === 'yes') : false,
                        isAvailable: availIdx !== -1 ? (row[availIdx].toLowerCase() !== 'false' && row[availIdx].toLowerCase() !== 'no') : true,
                        stockQuantity: stockIdx !== -1 ? parseInt(row[stockIdx]) : 100,
                        prepTime: prepIdx !== -1 ? parseInt(row[prepIdx]) : 15,
                        imageUrl: imageIdx !== -1 ? row[imageIdx] : ''
                    };

                    if (isNaN(item.price)) {
                        toast.error(`Invalid price on row ${i + 1}: ${row[priceIdx]}`);
                        return;
                    }

                    importItems.push(item);
                }

                setSubmitting(true);
                const res = await api.post('/api/menu/bulk-import', { items: importItems });
                toast.success(`Import success! Created ${res.data.itemsCreated} items, updated ${res.data.itemsUpdated} items, created ${res.data.categoriesCreated} categories.`);
                fetchData();
            } catch (err) {
                console.error('Import failed:', err);
                toast.error(err.response?.data?.message || 'Failed to import menu CSV.');
            } finally {
                setSubmitting(false);
                // Clear file input
                e.target.value = '';
            }
        };
        reader.readAsText(file);
    };

    const handleBulkDeleteCategories = () => {
        const idsArray = Array.from(selectedCategoryIds);
        if (idsArray.length === 0) return;

        setConfirmState({
            open: true,
            title: 'Bulk Delete Categories',
            message: `Are you sure you want to delete the ${idsArray.length} selected categories? This will fail if menu items are linked to them.`,
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    const res = await api.post('/api/menu/categories/bulk-delete', { ids: idsArray });
                    toast.success(res.data.message || `Successfully deleted categories.`);
                    setSelectedCategoryIds(new Set());
                    fetchData();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to bulk delete categories. Ensure no menu items are linked to them.');
                } finally {
                    setSubmitting(false);
                    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const handleBulkDeleteItems = () => {
        const idsArray = Array.from(selectedItemIds);
        if (idsArray.length === 0) return;

        setConfirmState({
            open: true,
            title: 'Bulk Delete Menu Items',
            message: `Are you sure you want to delete the ${idsArray.length} selected menu items?`,
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    const res = await api.post('/api/menu/items/bulk-delete', { ids: idsArray });
                    toast.success(res.data.message || `Successfully deleted items.`);
                    setSelectedItemIds(new Set());
                    fetchData();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to bulk delete items.');
                } finally {
                    setSubmitting(false);
                    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Block save if image is still uploading
        if (uploading) {
            toast.error('Please wait for the image to finish uploading.');
            return;
        }

        // Require image for new items
        if (!isEditing && !formData.imageUrl) {
            setImageRequired(true);
            toast.error('A menu item image is required. Please upload an image before saving.');
            return;
        }

        setImageRequired(false);
        setSubmitting(true);
        try {
            if (isEditing) {
                await api.put(`/api/menu/items/${editId}`, formData);
                toast.success('Item updated successfully');
            } else {
                await api.post('/api/menu/items', formData);
                toast.success('Item added successfully');
            }
            setFormData(initialForm);
            if (categories.length > 0) setFormData(prev => ({ ...prev, categoryId: categories[0].id }));
            setIsEditing(false);
            setEditId(null);
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to save item');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (isEditingCategory) {
                await api.put(`/api/menu/categories/${editCategoryId}`, categoryFormData);
                toast.success('Category updated successfully');
            } else {
                await api.post('/api/menu/categories', categoryFormData);
                toast.success('Category added successfully');
            }
            setCategoryFormData({ name: '', description: '', imageUrl: '', sortOrder: '' });
            setShowCategoryForm(false);
            setIsEditingCategory(false);
            setEditCategoryId(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save category');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditCategoryClick = (category) => {
        setCategoryFormData({
            name: category.name,
            description: category.description || '',
            imageUrl: category.image_url || '',
            sortOrder: category.sort_order || ''
        });
        setEditCategoryId(category.id);
        setIsEditingCategory(true);
        setShowCategoryForm(true);
    };

    const handleDeleteCategory = (id) => {
        setConfirmState({
            open: true,
            title: 'Delete Category',
            message: 'Are you sure you want to delete this category? This will fail if menu items are linked to it.',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await api.delete(`/api/menu/categories/${id}`);
                    toast.success('Category deleted successfully');
                    fetchData();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to delete category');
                } finally {
                    setSubmitting(false);
                    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const handleEditClick = (item) => {
        setFormData({
            name: item.name,
            description: item.description,
            price: item.price,
            categoryId: item.category_id,
            imageUrl: item.image_url || '',
            isVegetarian: item.is_vegetarian,
            isNonVegetarian: item.is_non_vegetarian || false,
            isSpicy: item.is_spicy,
            isSweet: item.is_sweet || false,
            prepTime: item.prep_time_minutes || '15',
            stockQuantity: item.stock_quantity || ''
        });
        setEditId(item.id);
        setIsEditing(true);
        setShowAddForm(true);
    };

    if (loading) return <Loading mode="page" message="Loading menu inventory..." />;

    // Filter Items
    const filteredItems = items.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = filterCategory === 'all' || (item.category_id && item.category_id.toString() === filterCategory.toString());
        return matchesSearch && matchesCategory;
    });

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    return (
        <div className="space-y-6">
            {submitting && (
                <div className="fixed inset-0 bg-black/60 z-[9999] flex flex-col items-center justify-center text-white backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 flex flex-col items-center max-w-xs shadow-2xl scale-in duration-200">
                        <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-4" />
                        <h3 className="font-bold text-lg mb-1 text-center">Processing Request</h3>
                        <p className="text-sm text-slate-400 text-center">Please wait while we update your menu inventory...</p>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                confirmText="Delete"
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ open: false, title: '', message: '', onConfirm: null })}
            />
            <div className="flex flex-col sm:flex-row justify-between sm:items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100 gap-4">
                <div className="flex space-x-6 overflow-x-auto whitespace-nowrap hide-scrollbar pb-1">
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={`text-lg font-bold pb-2 border-b-2 transition-colors ${activeTab === 'categories' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        Categories
                    </button>
                    <button
                        onClick={() => setActiveTab('items')}
                        className={`text-lg font-bold pb-2 border-b-2 transition-colors ${activeTab === 'items' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        Menu Items
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={downloadTemplate}
                        className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border rounded-xl transition-colors"
                        title="Download CSV Template"
                    >
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span>Template</span>
                    </button>
                    <button
                        onClick={handleExportCatalog}
                        className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border rounded-xl transition-colors"
                        title="Export Menu Catalog"
                    >
                        <Download className="w-4 h-4 text-gray-500" />
                        <span>Export</span>
                    </button>
                    <label
                        className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors ${submitting ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}
                        title="Import Menu CSV"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        <span>{submitting ? 'Importing...' : 'Import'}</span>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleImportCSV}
                            className="hidden"
                            disabled={submitting}
                        />
                    </label>

                    {activeTab === 'categories' && selectedCategoryIds.size > 0 && (
                        <button
                            onClick={handleBulkDeleteCategories}
                            disabled={submitting}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            <span>{submitting ? 'Deleting...' : `Delete Selected (${selectedCategoryIds.size})`}</span>
                        </button>
                    )}

                    {activeTab === 'items' && selectedItemIds.size > 0 && (
                        <button
                            onClick={handleBulkDeleteItems}
                            disabled={submitting}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            <span>{submitting ? 'Deleting...' : `Delete Selected (${selectedItemIds.size})`}</span>
                        </button>
                    )}

                    {activeTab === 'categories' && !showCategoryForm && (
                        <button
                            onClick={() => setShowCategoryForm(true)}
                            className="btn-primary flex items-center shadow-md text-xs py-2 px-3.5"
                        >
                            <Plus className="w-4 h-4 mr-1.5" />
                            Add Category
                        </button>
                    )}
                    {activeTab === 'items' && !showAddForm && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="btn-primary flex items-center shadow-md text-xs py-2 px-3.5"
                        >
                            <Plus className="w-4 h-4 mr-1.5" />
                            Add Item
                        </button>
                    )}
                </div>
            </div>

            {showCategoryForm && (
                <div className="card p-6 border-2 border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold">{isEditingCategory ? 'Edit Category' : 'Add New Category'}</h2>
                        <button
                            onClick={() => {
                                setShowCategoryForm(false);
                                setIsEditingCategory(false);
                                setEditCategoryId(null);
                                setCategoryFormData({ name: '', description: '', imageUrl: '', sortOrder: '' });
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            Cancel
                        </button>
                    </div>
                    <form onSubmit={handleCategorySubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Category Name</label>
                            <input type="text" required className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500" value={categoryFormData.name} onChange={e => setCategoryFormData({ ...categoryFormData, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Description (Optional)</label>
                            <input type="text" className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500" value={categoryFormData.description} onChange={e => setCategoryFormData({ ...categoryFormData, description: e.target.value })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1.5">Category Image</label>
                            <div className="flex items-center space-x-4 bg-slate-50 border border-slate-100 rounded-xl p-3">
                                {categoryFormData.imageUrl ? (
                                    <div className="relative w-14 h-14 bg-white rounded-lg border overflow-hidden flex items-center justify-center flex-shrink-0">
                                        <img src={categoryFormData.imageUrl} alt="Category preview" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => setCategoryFormData({ ...categoryFormData, imageUrl: '' })} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-4 h-4 flex items-center justify-center text-[8px] font-bold">✕</button>
                                    </div>
                                ) : (
                                    <label className="w-14 h-14 bg-white rounded-lg border-2 border-dashed border-slate-300 hover:border-primary-500 flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0">
                                        <ImageIcon className="text-slate-400 w-4 h-4" />
                                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'items', (url) => setCategoryFormData({ ...categoryFormData, imageUrl: url }))} className="hidden" disabled={uploading} />
                                    </label>
                                )}
                                <div className="flex-1 text-xs text-slate-400">
                                    {uploading ? (
                                        <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary...</span>
                                    ) : (
                                        <span>JPEG, PNG (max 2 MB)</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {isEditingCategory && (
                            <div>
                                <label className="block text-sm font-medium mb-1">Sort Order</label>
                                <input type="number" className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500" value={categoryFormData.sortOrder} onChange={e => setCategoryFormData({ ...categoryFormData, sortOrder: e.target.value })} />
                            </div>
                        )}
                        <div className="md:col-span-2 flex space-x-4 mt-2">
                            <button type="submit" disabled={submitting} className="btn-primary flex items-center min-w-[140px] justify-center">
                                {submitting ? (
                                    <Loading mode="button" message={isEditingCategory ? 'Updating...' : 'Adding...'} />
                                ) : (
                                    <>
                                        {isEditingCategory ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                        {isEditingCategory ? 'Update Category' : 'Add Category'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'categories' && !showCategoryForm && (
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        checked={categories.length > 0 && categories.every(c => selectedCategoryIds.has(c.id))}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedCategoryIds(new Set(categories.map(c => c.id)));
                                            } else {
                                                setSelectedCategoryIds(new Set());
                                            }
                                        }}
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sort Order</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {[...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(category => (
                                <tr key={category.id} className={selectedCategoryIds.has(category.id) ? 'bg-primary-50/30' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            checked={selectedCategoryIds.has(category.id)}
                                            onChange={(e) => {
                                                const newSelected = new Set(selectedCategoryIds);
                                                if (e.target.checked) {
                                                    newSelected.add(category.id);
                                                } else {
                                                    newSelected.delete(category.id);
                                                }
                                                setSelectedCategoryIds(newSelected);
                                            }}
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            {category.image_url && <img src={category.image_url} alt="" className="w-10 h-10 rounded-lg object-cover mr-3" />}
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                                <div className="text-sm text-gray-500">{category.description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {category.sort_order}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => handleEditCategoryClick(category)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            <Edit2 className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleDeleteCategory(category.id)} className="text-red-600 hover:text-red-900">
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {activeTab === 'items' && showAddForm && (
                <div className="card p-6 border-2 border-primary-100">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold">{isEditing ? 'Edit Item' : 'Add New Item'}</h2>
                        <button
                            onClick={() => {
                                setShowAddForm(false);
                                setIsEditing(false);
                                setFormData(initialForm);
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            Cancel
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                        <label className="block text-sm font-medium mb-1">Name</label>
                        <input type="text" required className="w-full px-4 py-2 border rounded-lg" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Category</label>
                        <select className="w-full px-4 py-2 border rounded-lg" value={formData.categoryId} onChange={e => setFormData({ ...formData, categoryId: e.target.value })}>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Price (₹)</label>
                        <input type="number" step="0.01" required className="w-full px-4 py-2 border rounded-lg" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Prep Time (mins)</label>
                        <input type="number" required className="w-full px-4 py-2 border rounded-lg" value={formData.prepTime} onChange={e => setFormData({ ...formData, prepTime: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Stock Quantity</label>
                        <input type="number" required className="w-full px-4 py-2 border rounded-lg" value={formData.stockQuantity} onChange={e => setFormData({ ...formData, stockQuantity: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea required className="w-full px-4 py-2 border rounded-lg" rows="2" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1.5">
                            Menu Item Image
                            {!isEditing && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <div className={`flex items-center space-x-4 rounded-xl p-3 border transition-colors ${
                            imageRequired && !formData.imageUrl
                                ? 'bg-red-50 border-red-300'
                                : 'bg-slate-50 border-slate-100'
                        }`}>
                            {formData.imageUrl ? (
                                <div className="relative w-14 h-14 bg-white rounded-lg border overflow-hidden flex items-center justify-center flex-shrink-0">
                                    <img src={formData.imageUrl} alt="Item preview" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => { setFormData({ ...formData, imageUrl: '' }); setImageRequired(true); }} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors w-4 h-4 flex items-center justify-center text-[8px] font-bold">✕</button>
                                </div>
                            ) : (
                                <label className={`w-14 h-14 bg-white rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors flex-shrink-0 ${
                                    imageRequired ? 'border-red-400 hover:border-red-500' : 'border-slate-300 hover:border-primary-500'
                                }`}>
                                    <ImageIcon className={`w-4 h-4 ${imageRequired ? 'text-red-400' : 'text-slate-400'}`} />
                                    <input type="file" accept="image/*" onChange={(e) => { setImageRequired(false); handleImageUpload(e, 'items', (url) => setFormData(prev => ({ ...prev, imageUrl: url }))); }} className="hidden" disabled={uploading} />
                                </label>
                            )}
                            <div className="flex-1 text-xs">
                                {uploading ? (
                                    <span className="text-primary-600 font-semibold animate-pulse">Uploading to Cloudinary... Please wait before saving.</span>
                                ) : imageRequired && !formData.imageUrl ? (
                                    <span className="text-red-500 font-semibold">Image is required — please upload a photo of the item.</span>
                                ) : formData.imageUrl ? (
                                    <span className="text-green-600 font-semibold">✓ Image uploaded successfully</span>
                                ) : (
                                    <span className="text-slate-400">JPEG, PNG (max 2 MB) — required for new items</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex space-x-6 py-2 flex-wrap">
                        <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.isVegetarian} onChange={e => setFormData({ ...formData, isVegetarian: e.target.checked })} />
                            <span>Vegetarian</span>
                        </label>
                        <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.isNonVegetarian} onChange={e => setFormData({ ...formData, isNonVegetarian: e.target.checked })} />
                            <span>Non-Vegetarian</span>
                        </label>
                        <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.isSpicy} onChange={e => setFormData({ ...formData, isSpicy: e.target.checked })} />
                            <span>Spicy</span>
                        </label>
                        <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={formData.isSweet} onChange={e => setFormData({ ...formData, isSweet: e.target.checked })} />
                            <span>Sweet</span>
                        </label>
                    </div>
                    <div className="md:col-span-2 flex space-x-4 mt-2">
                        <button type="submit" disabled={submitting || uploading} className="btn-primary flex items-center min-w-[150px] justify-center disabled:opacity-60 disabled:cursor-not-allowed">
                            {submitting ? (
                                <Loading mode="button" message={isEditing ? 'Updating...' : 'Adding...'} />
                            ) : uploading ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading Image...</>
                            ) : (
                                <>
                                    {isEditing ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                    {isEditing ? 'Update Item' : 'Add Item'}
                                </>
                            )}
                        </button>
                        {isEditing && !submitting && (
                            <button type="button" onClick={() => { setIsEditing(false); setFormData(initialForm); }} className="btn-secondary">
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>
            )}

            {activeTab === 'items' && (
                <div className="card overflow-hidden">
                    <div className="p-4 border-b bg-gray-50 flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <div className="w-full sm:w-1/3 text-xl font-bold text-gray-800">
                        Menu Items ({filteredItems.length})
                    </div>
                    <div className="flex w-full sm:w-2/3 space-x-4">
                        <input
                            type="text"
                            placeholder="Search items..."
                            className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="w-full sm:w-48 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left w-10">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    checked={currentItems.length > 0 && currentItems.every(i => selectedItemIds.has(i.id))}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedItemIds(new Set([...selectedItemIds, ...currentItems.map(i => i.id)]));
                                        } else {
                                            const newSelected = new Set(selectedItemIds);
                                            currentItems.forEach(i => newSelected.delete(i.id));
                                            setSelectedItemIds(newSelected);
                                        }
                                    }}
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentItems.map(item => (
                            <tr key={item.id} className={selectedItemIds.has(item.id) ? 'bg-primary-50/30' : ''}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        checked={selectedItemIds.has(item.id)}
                                        onChange={(e) => {
                                            const newSelected = new Set(selectedItemIds);
                                            if (e.target.checked) {
                                                newSelected.add(item.id);
                                            } else {
                                                newSelected.delete(item.id);
                                            }
                                            setSelectedItemIds(newSelected);
                                        }}
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover mr-3" />}
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                            {!item.is_available && (
                                                <span className="inline-block mt-0.5 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-semibold">
                                                    Out of Stock
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.category_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">₹{item.price}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleStockUpdate(item.id, Number(item.stock_quantity) - 1)}
                                            className="p-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                                            disabled={item.stock_quantity <= 0}
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <input
                                            type="number"
                                            value={item.stock_quantity}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setItems(currentItems => currentItems.map(i => i.id === item.id ? { ...i, stock_quantity: val } : i));
                                            }}
                                            onBlur={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val) && val >= 0) {
                                                    handleStockUpdate(item.id, val);
                                                }
                                            }}
                                            className={`w-14 text-center text-sm font-bold border rounded p-1 ${item.stock_quantity <= 5 ? 'text-red-600 border-red-300' : 'text-gray-900'}`}
                                            min="0"
                                        />
                                        <button
                                            onClick={() => handleStockUpdate(item.id, Number(item.stock_quantity) + 1)}
                                            className="p-1 rounded bg-gray-100 hover:bg-gray-200"
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        onClick={() => handleToggleAvailability(item.id, item.is_available)}
                                        className={`px-3 py-1 rounded-full text-xs font-medium ${item.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                                    >
                                        {item.is_available ? 'In Stock' : 'Out of Stock'}
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => handleEditClick(item)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                        <Edit2 className="w-5 h-5" />
                                    </button>
                                    <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900">
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50">
                        <div className="text-sm text-gray-500">
                            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredItems.length)}</span> of <span className="font-medium">{filteredItems.length}</span> results
                        </div>
                        <div className="flex space-x-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
}
